const AVATAR_SIZE = 256
const MAX_AVATAR_LENGTH = 280_000

interface DrawableImage {
    source: CanvasImageSource
    width: number
    height: number
    dispose: () => void
}

function loadWithImageElement(file: File): Promise<DrawableImage> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const image = new Image()
        image.onload = () => resolve({
            source: image,
            width: image.naturalWidth,
            height: image.naturalHeight,
            dispose: () => URL.revokeObjectURL(url),
        })
        image.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error('无法读取图片'))
        }
        image.src = url
    })
}

async function loadDrawable(file: File): Promise<DrawableImage> {
    if (typeof createImageBitmap !== 'function') { return loadWithImageElement(file) }
    try {
        const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
        return {
            source: bitmap,
            width: bitmap.width,
            height: bitmap.height,
            dispose: () => bitmap.close(),
        }
    }
    catch {
        return loadWithImageElement(file)
    }
}

function canvasToDataUrl(canvas: HTMLCanvasElement, quality: number): Promise<string> {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) { reject(new Error('无法压缩图片')); return }
            const reader = new FileReader()
            reader.onload = () => resolve(String(reader.result || ''))
            reader.onerror = () => reject(new Error('无法读取压缩后的图片'))
            reader.readAsDataURL(blob)
        }, 'image/jpeg', quality)
    })
}

export async function prepareAvatar(file: File): Promise<string> {
    if (!file.type.startsWith('image/')) { throw new Error('请选择图片文件') }
    if (file.size > 8 * 1024 * 1024) { throw new Error('图片不能超过 8MB') }

    const image = await loadDrawable(file)
    try {
        if (!image.width || !image.height) { throw new Error('图片尺寸无效') }
        const canvas = document.createElement('canvas')
        canvas.width = AVATAR_SIZE
        canvas.height = AVATAR_SIZE
        const context = canvas.getContext('2d')
        if (!context) { throw new Error('当前浏览器无法处理图片') }

        const cropSize = Math.min(image.width, image.height)
        const sourceX = (image.width - cropSize) / 2
        const sourceY = (image.height - cropSize) / 2
        context.drawImage(image.source, sourceX, sourceY, cropSize, cropSize, 0, 0, AVATAR_SIZE, AVATAR_SIZE)

        for (const quality of [0.86, 0.76, 0.66]) {
            const dataUrl = await canvasToDataUrl(canvas, quality)
            if (dataUrl.length <= MAX_AVATAR_LENGTH) { return dataUrl }
        }
        throw new Error('图片压缩后仍然过大，请换一张图片')
    }
    finally {
        image.dispose()
    }
}
